import { FocusEventHandler, useRef, useState, useEffect } from 'react';
import AutoComplete, { AutoCompleteInputVariant, IAutoCompleteProps } from '.';
import { scrollToElement } from '@/shared/helpers/scroll';
import { PopoverAutoComplete } from './popover-auto-complete';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

type IProps<TOption> = IAutoCompleteProps<TOption> & {
    popoverInputVariant?: AutoCompleteInputVariant;
    popoverPlaceholder?: string;
};

export const AdvancedAutoComplete = <TOption,>(props: IProps<TOption>) => {
    const { loadOptionsList, onSelect, uniqueIdentifier, scrollToTitle, popoverInputVariant, error, popoverPlaceholder } = props;
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [isPopoverOpen, setIsPopoverOpen] = useState('');
    const [isSearched, setIsSearched] = useState(false);
    const [isVoicePopover, setIsVoicePopover] = useState(false);
    const popoverOpenInitialY = useRef<number | null>(null);
    const wrapperRef = useRef<HTMLDivElement | null>(null);

    const handlePopoverClose = () => {
        setIsPopoverOpen('');
        setIsVoicePopover(false);

        if (scrollToTitle && wrapperRef.current) {
            scrollToElement(wrapperRef.current, 150, 0);
        } else if (popoverOpenInitialY.current !== null) {
            setTimeout(() => window.scrollTo(0, popoverOpenInitialY.current || 0), 0);
        }
    };

    const localLoadOptionsList = async (query: string, signal: AbortSignal) => {
        const res = await loadOptionsList(query, signal);
        setIsSearched(Boolean(res?.length));
        return res;
    };

    const handleOnFocus: FocusEventHandler<HTMLDivElement> = e => {
        if (isMobile) {
            setIsPopoverOpen('keyboard');
        }
        props.onFocus?.(e);
    };

    return (
        <>
            {isPopoverOpen && (
                <PopoverAutoComplete
                    {...props}
                    defaultVoiceOpen={isVoicePopover}
                    onSelect={onSelect}
                    loadOptionsList={localLoadOptionsList}
                    uniqueIdentifier={uniqueIdentifier}
                    onPopoverClose={handlePopoverClose}
                    searched={isSearched}
                    inputWithBorder
                    inputVariant={popoverInputVariant}
                    onInput={e => {
                        if (!(e.target as HTMLInputElement).value) {
                            setIsSearched(false);
                        }
                        props.onInput?.(e);
                    }}
                    placeholder={popoverPlaceholder || props.placeholder}
                    initialIntent={isPopoverOpen}
                />
            )}
            <AutoComplete
                {...props}
                onFocus={handleOnFocus}
                onMicClick={() => {
                    setIsPopoverOpen('microphone');
                }}
                error={error}
            />
        </>
    );
};
